import { useQuery as useZeroQuery } from "@rocicorp/zero/react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router";

import { type RegisteredUser, getRegisteredUsers } from "@/api/rooms";
import ClosableCard from "@/components/closable-card";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLogin } from "@/hooks/use-login";
import { useRoom } from "@/hooks/use-room";
import { useZero } from "@/hooks/use-zero";

export default function RoomDetail() {
  const params = useParams();
  const { user: sessionUser } = useLogin();
  const { leaveRoom } = useRoom();

  // TODO: improve type safety?
  const roomId = params["roomId"] as string;

  const z = useZero();
  const roomQuery = z.query.room.where("id", roomId).one();
  const [room] = useZeroQuery(roomQuery);

  const { data: activeUsers = [] } = useQuery({
    queryKey: ["activeUsers", roomId],
    queryFn: async () => {
      const response = await getRegisteredUsers(roomId);
      return response.users;
    },
    refetchInterval: 2000,
  });

  if (!room) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Room Not Found</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Room with ID {roomId} not found or is closed.</p>
          <Link to="/rooms">
            <Button variant="outline" size="sm">
              Leave
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <ClosableCard className="relative" title={room.name}>
      <CardContent className="space-y-4">
        {room.status === "closed" && (
          <div className="bg-destructive/10 border border-destructive/20 rounded p-3">
            <p className="text-destructive text-sm">
              This room has been closed and is no longer accepting updates.
            </p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium">Status:</span> {room.status}
          </div>
          <div>
            <span className="font-medium">Active Users</span>{" "}
            {activeUsers.length}
          </div>
          <div>
            <span className="font-medium">Created:</span>{" "}
            {new Date(room.createdOn).toLocaleString()}
          </div>
          <div className="flex flex-row space-x-2">
            <div className="font-medium">Users:</div>
            <div className="space-x-2">
              {activeUsers.length === 0 ? (
                <p className="text-muted-foreground text-sm">No active users</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {activeUsers.map((user: RegisteredUser) => (
                    <div
                      key={user.userId || user.registeredAt}
                      className="bg-muted px-2 py-1 rounded text-xs"
                    >
                      {user.userId === sessionUser?.id
                        ? "You"
                        : user.userId?.slice(0, 8) || "Anon"}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <Button variant="outline" onClick={leaveRoom}>
          Leave Room
        </Button>
      </CardContent>
    </ClosableCard>
  );
}
